import { useState } from "react";
import { FilePenLine } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, cn } from "@ncp/ui";

/** One clause of a proposed mark-up: the risk it answers, how serious it is, the wording today and the wording proposed. */
export interface RedlineEntry {
  clause: string;
  risk: string | null;
  rating: "high" | "medium" | "low" | null;
  current: string | null;
  proposed: string;
}

const text = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
const RATINGS = ["high", "medium", "low"] as const;

/** The mark-up in an agent's structured output: the first array whose items carry `proposedWording` (a contract review's risk register). */
export function redlinesOf(structured: unknown): RedlineEntry[] {
  if (!structured || typeof structured !== "object" || Array.isArray(structured)) return [];
  for (const value of Object.values(structured as Record<string, unknown>)) {
    if (!Array.isArray(value)) continue;
    const entries = value
      .filter((x): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x) && !!text((x as Record<string, unknown>).proposedWording))
      .map((x) => {
        const rating = text(x.rating)?.toLowerCase();
        return {
          clause: text(x.clause) ?? "",
          risk: text(x.risk),
          rating: RATINGS.find((r) => r === rating) ?? null,
          current: text(x.currentWording),
          proposed: text(x.proposedWording)!,
        };
      });
    if (entries.length) return entries;
  }
  return [];
}

const ratingVariant = { high: "danger", medium: "warning", low: "outline" } as const;
const ratingRank = { high: 0, medium: 1, low: 2 } as const;

/** Clause-by-clause redline: current wording struck through, proposed wording as an insertion, highest risks first. */
export function RedlinePanel({ entries, className }: { entries: RedlineEntry[]; className?: string }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const sorted = [...entries].sort((a, b) => (a.rating ? ratingRank[a.rating] : 3) - (b.rating ? ratingRank[b.rating] : 3));
  const shown = all ? sorted : sorted.slice(0, 4);
  const high = entries.filter((e) => e.rating === "high").length;
  return (
    <div className={cn("rounded-xl border border-sand-200 bg-white p-3", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-aeblack-900"><FilePenLine className="size-3.5 text-aegold-700" /> {t("ai.redlines.title")}</p>
        <span className="text-[11px] text-muted-foreground">{t("ai.redlines.count", { count: entries.length })}</span>
        {high ? <Badge variant="danger" size="sm">{t("ai.redlines.highCount", { count: high })}</Badge> : null}
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{t("ai.redlines.hint")}</p>
      <ol className="mt-2 space-y-2">
        {shown.map((e, i) => (
          <li key={`${e.clause}-${i}`} className="rounded-lg border border-sand-200 bg-sand-50/50 p-2.5 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{e.clause || t("ai.redlines.clause", { n: i + 1 })}</span>
              {e.rating ? <Badge variant={ratingVariant[e.rating]} size="sm">{t(`ai.redlines.rating.${e.rating}`)}</Badge> : null}
            </div>
            {e.risk ? <p className="mt-1 text-xs text-muted-foreground">{e.risk}</p> : null}
            <div className="mt-1.5 space-y-1 leading-relaxed" dir="auto">
              {e.current
                ? <p><span className="sr-only">{t("ai.redlines.current")}: </span><del className="rounded bg-danger-50 px-0.5 text-danger-700 decoration-danger-400">{e.current}</del></p>
                : <p className="text-[11px] italic text-muted-foreground">{t("ai.redlines.notQuoted")}</p>}
              <p><span className="sr-only">{t("ai.redlines.proposed")}: </span><ins className="rounded bg-success-50 px-0.5 text-success-700 underline decoration-success-400 underline-offset-2">{e.proposed}</ins></p>
            </div>
          </li>
        ))}
      </ol>
      {entries.length > 4 ? (
        <button type="button" onClick={() => setAll(!all)} className="mt-2 text-xs font-medium text-aegold-700 hover:underline">
          {all ? t("ai.redlines.showFewer") : t("ai.redlines.showAll", { count: entries.length })}
        </button>
      ) : null}
    </div>
  );
}
